import React from 'react';
import { FileText, Calendar, MapPin, Tag } from 'lucide-react';
import type { LeaseApplication } from '../../types/lease-application';
import { formatDate } from '../../utils/dateUtils';

interface LeaseApplicationCardProps {
  application: LeaseApplication;
  onClick: () => void;
}

export default function LeaseApplicationCard({ application, onClick }: LeaseApplicationCardProps) {
  const getStatusColor = () => {
    if (application.app_final === 'Y') return 'bg-green-100 text-green-800';
    if (application.app_approv_date) return 'bg-blue-100 text-blue-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const getStatusText = () => {
    if (application.app_final === 'Y') return 'Completed';
    if (application.app_approv_date) return 'Approved';
    return 'Pending';
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-gray-400" />
          <span className="text-lg font-medium text-gray-900">
            Application #{application.app_id}
          </span>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}>
          {getStatusText()}
        </span>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-gray-600">
          <MapPin className="h-4 w-4" />
          <span>Lot: {application.app_lot}</span>
        </div>

        <div className="flex items-center gap-2 text-gray-600">
          <Tag className="h-4 w-4" />
          <span>Type: {application.app_type}</span>
        </div>

        <div className="flex items-center gap-2 text-gray-600">
          <Calendar className="h-4 w-4" />
          <span>Submitted: {formatDate(application.app_date)}</span>
        </div>

        {application.app_approv_date && (
          <div className="flex items-center gap-2 text-gray-600">
            <Calendar className="h-4 w-4" />
            <span>Approved: {formatDate(application.app_approv_date)}</span>
          </div>
        )}
      </div>

      {application.app_desc && (
        <p className="mt-4 text-sm text-gray-500 line-clamp-2">
          {application.app_desc}
        </p>
      )}
    </div>
  );
}
